import { DeviceType } from '$lib/constants';
import type { Component } from 'svelte';

// Device-specific components (lazy loaded)
const CoffeeTechSettings = () => import('$lib/components/settings/CoffeeTechSettings.svelte');
const DiedrichSettings = () => import('$lib/components/settings/DiedrichSettings.svelte');
const LoringSettings = () => import('$lib/components/settings/LoringSettings.svelte');
const SivetzSettings = () => import('$lib/components/settings/SivetzSettings.svelte');
const SanFranciscanSettings = () => import('$lib/components/settings/SanFranciscanSettings.svelte');
const ModbusRTUSettings = () => import('$lib/components/settings/ModbusRTUSettings.svelte');
const CoffedModbusSettings = () => import('$lib/components/settings/CoffedModbusSettings.svelte');
const PhidgetsRTDSettings = () => import('$lib/components/settings/PhidgetsRTDSettings.svelte');

// Yoshan icon (lazy loaded)
const YoshanIcon = () => import('$lib/components/icons/Yoshan.svelte');

// Device-specific icons (lazy loaded)
const ModbusIcon = () => import('$lib/components/icons/Modbus.svelte');
const SivetzIcon = () => import('$lib/components/icons/Sivetz.svelte');
const PhidgetsIcon = () => import('$lib/components/icons/Phidgets.svelte');
const SanFranciscanIcon = () => import('$lib/components/icons/SanFranciscan.svelte');

export enum ThermocoupleKey {
  TC1 = 'TC1',
  TC2 = 'TC2',
  TC3 = 'TC3',
  TC4 = 'TC4'
}

export type DeviceLabels = Record<ThermocoupleKey, string | null>;

export interface DeviceConfig {
  type: DeviceType;
  label: string;
  icon: () => Promise<{ default: Component }>;
  hasModbusSettings: boolean;
  hasBurnerChart: boolean;
  hasHardcodedLabels: boolean;
  settingsComponent?: () => Promise<{ default: Component }>;
  labels?: DeviceLabels;
  requiresTauri?: boolean;
  modbusPort?: number;
  /**
   * Prefix shown for each sensor in the labels UI (e.g. "TC" for
   * thermocouples, "RTD" for RTD probes). Defaults to "TC".
   */
  sensorLabel?: string;
  /**
   * Whether the device exposes a thermocouple type (J/K) toggle in its
   * settings. RTD and other non-thermocouple sensors should set this to
   * false. Defaults to true.
   */
  hasTcTypeToggle?: boolean;
}

export const DEVICE_REGISTRY: Record<DeviceType, DeviceConfig> = {
  [DeviceType.PHIDGETS]: {
    type: DeviceType.PHIDGETS,
    label: 'Phidgets',
    icon: PhidgetsIcon,
    hasModbusSettings: false,
    hasBurnerChart: false,
    hasHardcodedLabels: false,
    requiresTauri: false
  },
  [DeviceType.PHIDGETS_RTD]: {
    type: DeviceType.PHIDGETS_RTD,
    label: 'Phidgets RTD',
    icon: PhidgetsIcon,
    hasModbusSettings: false,
    hasBurnerChart: false,
    hasHardcodedLabels: false,
    requiresTauri: false,
    sensorLabel: 'RTD',
    hasTcTypeToggle: false,
    settingsComponent: PhidgetsRTDSettings
  },
  [DeviceType.FAKE]: {
    type: DeviceType.FAKE,
    label: 'Simulator',
    icon: async () => ({ default: (await import('@lucide/svelte')).FlaskConical }),
    hasModbusSettings: false,
    hasBurnerChart: false,
    hasHardcodedLabels: false,
    requiresTauri: false
  },
  [DeviceType.RLBTLE]: {
    type: DeviceType.RLBTLE,
    label: 'RoastLog Bluetooth',
    icon: async () => ({ default: (await import('@lucide/svelte')).Bluetooth }),
    hasModbusSettings: false,
    hasBurnerChart: false,
    hasHardcodedLabels: false,
    requiresTauri: false
  },
  [DeviceType.RLUSB]: {
    type: DeviceType.RLUSB,
    label: 'RoastLog USB',
    icon: async () => ({ default: (await import('@lucide/svelte')).Usb }),
    hasModbusSettings: false,
    hasBurnerChart: false,
    hasHardcodedLabels: false,
    requiresTauri: false
  },
  [DeviceType.LORING]: {
    type: DeviceType.LORING,
    label: 'Loring',
    icon: ModbusIcon,
    hasModbusSettings: true,
    hasBurnerChart: true,
    hasHardcodedLabels: true,
    settingsComponent: LoringSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Inlet',
      TC3: 'Return',
      TC4: 'Stack'
    },
    requiresTauri: true,
    modbusPort: 502
  },
  [DeviceType.DIEDRICH]: {
    type: DeviceType.DIEDRICH,
    label: 'Diedrich',
    icon: ModbusIcon,
    hasModbusSettings: true,
    hasBurnerChart: true,
    hasHardcodedLabels: true,
    settingsComponent: DiedrichSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Inlet',
      TC3: 'Exhaust',
      TC4: null
    },
    requiresTauri: true,
    modbusPort: 8010
  },
  [DeviceType.SAN_FRANCISCAN]: {
    type: DeviceType.SAN_FRANCISCAN,
    label: 'San Franciscan (Modbus TCP)',
    icon: SanFranciscanIcon,
    hasModbusSettings: true,
    hasBurnerChart: true,
    hasHardcodedLabels: true,
    settingsComponent: SanFranciscanSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Inlet',
      TC3: 'Exhaust',
      TC4: null
    },
    requiresTauri: true,
    modbusPort: 502
  },
  [DeviceType.SAN_FRANCISCAN_RTU]: {
    type: DeviceType.SAN_FRANCISCAN_RTU,
    label: 'San Franciscan (RTU)',
    icon: SanFranciscanIcon,
    hasModbusSettings: true, // RTU uses serial port settings instead
    hasBurnerChart: false, // RTU device doesn't support burner control
    hasHardcodedLabels: true,
    settingsComponent: ModbusRTUSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Environment',
      TC3: null,
      TC4: null
    },
    requiresTauri: true
  },
  [DeviceType.COFFEE_TECH]: {
    type: DeviceType.COFFEE_TECH,
    label: 'Coffee Tech',
    icon: ModbusIcon,
    hasModbusSettings: true,
    hasBurnerChart: true,
    hasHardcodedLabels: true,
    settingsComponent: CoffeeTechSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Drum',
      TC3: 'Exhaust',
      TC4: null
    },
    requiresTauri: true,
    modbusPort: 502
  },
  [DeviceType.SIVETZ]: {
    type: DeviceType.SIVETZ,
    label: 'Sivetz',
    icon: SivetzIcon,
    hasModbusSettings: true,
    hasBurnerChart: false,
    hasHardcodedLabels: true,
    settingsComponent: SivetzSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Inlet',
      TC3: 'Exhaust',
      TC4: null
    },
    requiresTauri: true,
    modbusPort: 502
  },
  [DeviceType.PRIMO]: {
    type: DeviceType.PRIMO,
    label: 'Primo',
    icon: ModbusIcon,
    hasModbusSettings: true, // RTU uses serial port settings instead
    hasBurnerChart: true, // Primo supports gas valve control
    hasHardcodedLabels: true,
    settingsComponent: ModbusRTUSettings,
    labels: {
      TC1: 'Bean',
      TC2: null,
      TC3: null,
      TC4: null
    },
    requiresTauri: true
  },
  [DeviceType.COFFED_SR30]: {
    type: DeviceType.COFFED_SR30,
    label: 'Coffed SR30 (RTU)',
    icon: ModbusIcon,
    hasModbusSettings: true, // RTU uses serial port settings
    hasBurnerChart: false, // Coffed SR30 doesn't support burner control
    hasHardcodedLabels: true,
    settingsComponent: ModbusRTUSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Exhaust',
      TC3: null,
      TC4: null
    },
    requiresTauri: true
  },
  [DeviceType.COFFED_MODBUS]: {
    type: DeviceType.COFFED_MODBUS,
    label: 'Coffed SR30 (Modbus TCP)',
    icon: ModbusIcon,
    hasModbusSettings: true,
    hasBurnerChart: false, // Coffed SR30 doesn't support burner control
    hasHardcodedLabels: true,
    settingsComponent: CoffedModbusSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Exhaust',
      TC3: null,
      TC4: null
    },
    requiresTauri: true,
    modbusPort: 502
  },
  [DeviceType.YOSHAN_RTU]: {
    type: DeviceType.YOSHAN_RTU,
    label: 'Yoshan DY',
    icon: YoshanIcon,
    hasModbusSettings: true,
    hasBurnerChart: false,
    hasHardcodedLabels: true,
    settingsComponent: ModbusRTUSettings,
    labels: {
      TC1: 'Bean',
      TC2: 'Environment',
      TC3: null,
      TC4: null
    },
    requiresTauri: true
  }
};

// Helper functions
export const getDeviceConfig = (type: DeviceType): DeviceConfig => {
  const config = DEVICE_REGISTRY[type];
  if (!config) {
    console.warn(`Device type ${type} not found in DEVICE_REGISTRY`);
    // Return a default config to prevent crashes
    return {
      type,
      label: 'Unknown Device',
      icon: async () => ({ default: (await import('@lucide/svelte')).AlertTriangle }),
      hasModbusSettings: false,
      hasBurnerChart: false,
      hasHardcodedLabels: false,
      requiresTauri: false
    };
  }
  return config;
};

export const getDeviceLabel = (type: DeviceType): string => {
  const config = DEVICE_REGISTRY[type];
  if (!config) {
    console.warn(`Device type ${type} not found in DEVICE_REGISTRY`);
    return 'Unknown Device';
  }
  return config.label;
};

export const getDeviceLabels = (type: DeviceType): DeviceLabels | undefined => {
  const config = DEVICE_REGISTRY[type];
  if (!config) {
    console.warn(`Device type ${type} not found in DEVICE_REGISTRY`);
    return undefined;
  }
  return config.labels;
};

export const getAvailableDevices = (
  isTauri: boolean = false,
  isDemo: boolean = false
): DeviceConfig[] => {
  return Object.values(DEVICE_REGISTRY)
    .filter((config) => {
      if (config.requiresTauri && !isTauri) return false;
      if (config.type === DeviceType.FAKE && !isDemo) return false;
      return true;
    })
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
};
